import { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ScatterController,
  CategoryScale,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  ScatterController
);

export default function IntonationIntensityPlot({ intensity }) {
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const xs = intensity?.intensity?.xs || [];
  const ys = intensity?.intensity?.ys || [];
 
  if (!xs.length) return null;

  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);
  const yRange = yMax - yMin;

  const datasets = [];

  // 🔷 Main intensity line - optimized for mobile
  datasets.push({
    label: "Intensity (dB)",
    data: xs.map((x, i) => ({ x, y: ys[i] })),
    borderColor: "#2563eb",
    borderWidth: isMobile ? 1.5 : 2,
    tension: isMobile ? 0.2 : 0.4,
    pointRadius: 0,
    showLine: true,
    datalabels: { display: false },
  });

  const data = { datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: isMobile ? 1 : Math.max(2, window.devicePixelRatio),
    animation: false,
    scales: {
      x: {
        type: "linear",
        grid: { display: true, color: "rgba(0,0,0,0.05)" },
        ticks: { display: false },
        title: { 
          display: !isMobile, 
          text: "Time (seconds)",
          font: { size: isMobile ? 12 : 14 }
        }
      },
      y: {
        min: 0,
        max: yMax + yRange * 0.1,
        grid: { color: "rgba(0,0,0,0.05)" },
        title: { 
          display: true, 
          text: "Intensity (dB)", 
          font: { size: isMobile ? 12 : 16 } 
        },
        ticks: { 
          font: { size: isMobile ? 10 : 14 } 
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: !isMobile,
        mode: "nearest", 
        intersect: true 
      },
      datalabels: { display: false },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  return (
    <div className="flex flex-col gap-2 w-full min-w-0">
      {/* 📉 Chart wrapper: responsive and mobile optimized */}
      <div className={`w-full min-w-0 overflow-x-auto bg-white rounded-lg border border-gray-200 p-2 ${
        isMobile ? 'h-48' : 'h-64'
      }`}>
        <Line data={data} options={options} />
      </div>

      {/* Mobile info text */}
      {isMobile && (
        <p className="text-xs text-gray-500 text-center">
          Pinch to zoom for details
        </p>
      )}
    </div>
  );
}